import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ArrowLeft, FileText, AlertTriangle } from 'lucide-react';

const TermsOfServicePage = () => {
  const lastUpdated = "8 July 2026";
  
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#5DBDB3]/10 flex items-center justify-center">
            <FileText className="w-6 h-6 text-[#5DBDB3]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>
        </div>
        
        {/* Critical Health Disclaimer */}
        <Alert variant="destructive" className="mb-8 border-2">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-lg font-bold">IMPORTANT HEALTH DISCLAIMER</AlertTitle>
          <AlertDescription className="mt-2">
            <p className="mb-2">
              <strong>Seen Beyond the Label is NOT a medical device and should NOT be relied upon for medical decisions.</strong>
            </p>
            <p className="mb-2">
              Our Service provides informational analysis only. It is NOT a substitute for:
            </p>
            <ul className="list-disc pl-6 mb-2">
              <li>Reading product labels yourself</li>
              <li>Professional medical advice from your doctor or allergist</li>
              <li>Proper allergy testing and diagnosis</li>
              <li>Emergency medical treatment</li>
            </ul>
            <p className="font-bold">
              If you have severe allergies (anaphylaxis risk), ALWAYS read labels yourself, carry your EpiPen/adrenaline 
              auto-injector, and consult healthcare professionals. Never rely solely on this app.
            </p>
          </AlertDescription>
        </Alert>
        
        <Card>
          <CardContent className="prose prose-slate max-w-none p-8">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground">
                By accessing or using Seen Beyond the Label (&quot;Service&quot;), you agree to be bound by these Terms of Service 
                (&quot;Terms&quot;). If you do not agree to these Terms, you may not use the Service. These Terms constitute a 
                legally binding agreement between you and Seen Beyond the Label Ltd (&quot;Company&quot;, &quot;we&quot;, &quot;our&quot;, &quot;us&quot;), 
                a company registered in England and Wales.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground mb-4">
                Seen Beyond the Label provides:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Product barcode scanning and ingredient lookup</li>
                <li>AI-powered ingredient analysis</li>
                <li>Potential allergen identification</li>
                <li>Hidden ingredient detection</li>
                <li>Festival wristband management for event organisers</li>
              </ul>
            </section>
            
            <section className="mb-8 bg-amber-50 dark:bg-amber-950/20 p-6 rounded-lg border border-amber-200 dark:border-amber-800">
              <h2 className="text-xl font-semibold mb-4 text-amber-800 dark:text-amber-200">
                3. LIMITATION OF LIABILITY - PLEASE READ CAREFULLY
              </h2>
              
              <h3 className="text-lg font-medium mb-2 text-amber-700 dark:text-amber-300">3.1 No Medical Advice</h3>
              <p className="text-amber-900 dark:text-amber-100 mb-4">
                The Service provides INFORMATIONAL CONTENT ONLY. It does not provide medical advice, diagnosis, or treatment. 
                The information provided should not be used as a substitute for professional medical advice. Always consult 
                with a qualified healthcare provider regarding any medical conditions or allergies.
              </p>
              
              <h3 className="text-lg font-medium mb-2 text-amber-700 dark:text-amber-300">3.2 Accuracy Disclaimer</h3>
              <p className="text-amber-900 dark:text-amber-100 mb-4">
                While we strive for accuracy, we CANNOT GUARANTEE that:
              </p>
              <ul className="list-disc pl-6 text-amber-900 dark:text-amber-100 mb-4">
                <li>All ingredients will be correctly identified</li>
                <li>All allergens will be detected</li>
                <li>Product information is current (manufacturers may change formulations)</li>
                <li>AI analysis is 100% accurate</li>
                <li>Cross-contamination risks are identified</li>
                <li>Hidden allergens under proprietary names are always detected</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2 text-amber-700 dark:text-amber-300">3.3 Limitation of Liability</h3>
              <p className="text-amber-900 dark:text-amber-100 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
              </p>
              <ul className="list-disc pl-6 text-amber-900 dark:text-amber-100 mb-4">
                <li>
                  The Company shall NOT be liable for any indirect, incidental, special, consequential, or punitive damages, 
                  including but not limited to loss of profits, data, or goodwill.
                </li>
                <li>
                  The Company&apos;s total liability for any claims arising from or related to the Service shall not exceed 
                  the amount you paid to us in the 12 months preceding the claim, or £100, whichever is greater.
                </li>
                <li>
                  We do not exclude or limit liability for death or personal injury caused by our negligence, fraud, 
                  or any other liability that cannot be excluded by law.
                </li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2 text-amber-700 dark:text-amber-300">3.4 User Responsibility</h3>
              <p className="text-amber-900 dark:text-amber-100">
                YOU ACKNOWLEDGE AND AGREE THAT:
              </p>
              <ul className="list-disc pl-6 text-amber-900 dark:text-amber-100">
                <li>You are solely responsible for verifying ingredient information</li>
                <li>You must always read physical product labels</li>
                <li>You should not consume any product if unsure about its safety</li>
                <li>You must maintain appropriate medical care and carry emergency medication if prescribed</li>
                <li>The Service is a supplementary tool, not a replacement for proper allergy management</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify, defend, and hold harmless the Company and its officers, directors, employees, 
                and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including 
                reasonable legal fees) arising out of or in any way connected with your use of the Service, your 
                violation of these Terms, or your violation of any rights of another person or entity.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. User Accounts</h2>
              <p className="text-muted-foreground mb-4">
                When you create an account, you agree to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your password</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorised access</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. Subscriptions and Payments</h2>
              <p className="text-muted-foreground mb-4">
                Paid subscriptions are billed in advance on a monthly basis. You may cancel at any time, but refunds 
                are not provided for partial months. Prices are in GBP (£) and include VAT where applicable.
              </p>
              <p className="text-muted-foreground">
                We reserve the right to change subscription prices with 30 days&apos; notice.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. Acceptable Use</h2>
              <p className="text-muted-foreground mb-4">You agree NOT to:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorised access to our systems</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Submit false information to our databases</li>
                <li>Resell or redistribute the Service without permission</li>
                <li>Use automated systems to access the Service excessively</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Intellectual Property</h2>
              <p className="text-muted-foreground">
                The Service and its original content, features, and functionality are owned by Seen Beyond the Label Ltd 
                and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, 
                or distribute any part of the Service without our express written consent.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. Third-Party Services</h2>
              <p className="text-muted-foreground">
                Our Service may contain links to or integrations with third-party services (e.g., Open Food Facts, 
                payment processors). We are not responsible for the content, privacy policies, or practices of these 
                third parties.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. Community Contributions</h2>
              <p className="text-muted-foreground">
                If you contribute product information to our database, you grant us a non-exclusive, royalty-free, 
                perpetual license to use, modify, and distribute that information. You represent that you have the 
                right to submit such information and that it is accurate to the best of your knowledge.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. Termination</h2>
              <p className="text-muted-foreground">
                We may terminate or suspend your account and access to the Service immediately, without prior notice, 
                for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, 
                or for any other reason at our sole discretion.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground">
                THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR 
                IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
                PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR 
                ERROR-FREE.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with the laws of England and Wales. 
                Any disputes arising from these Terms or the Service shall be subject to the exclusive jurisdiction 
                of the courts of England and Wales.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">14. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms at any time. We will provide notice of material changes 
                by posting the updated Terms on this page. Your continued use of the Service after changes constitutes 
                acceptance of the new Terms.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">15. Severability</h2>
              <p className="text-muted-foreground">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue 
                in full force and effect.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">16. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For questions about these Terms, contact us:
              </p>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm"><strong>Company:</strong> Seen Beyond the Label Ltd</p>
                <p className="text-sm"><strong>Email:</strong> legal@seenbeyondthelabel.co.uk</p>
                <p className="text-sm"><strong>Address:</strong> [Your registered address]</p>
                <p className="text-sm"><strong>Company Number:</strong> [Your Companies House number]</p>
              </div>
            </section>
          </CardContent>
        </Card>
        
        {/* Second Disclaimer */}
        <Alert className="mt-8 border-[#F08B7D]">
          <AlertTriangle className="h-4 w-4 text-[#F08B7D]" />
          <AlertTitle>Remember</AlertTitle>
          <AlertDescription>
            Always read physical product labels. If you have severe allergies, consult your doctor and always carry 
            your prescribed emergency medication. This app is a helpful tool but is not a replacement for proper 
            medical care and vigilance.
          </AlertDescription>
        </Alert>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-border mt-12 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Seen Beyond the Label Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
